import { Box, BoxProps, Stack, TextField, Typography, useTheme } from "@mui/material";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { FeatureFlagged } from "@/Components/FeatureFlagging";
import { SendIcon } from "@/Components/Icons/SendIcon";
import { Button, WarcButton } from "@/Components/v2/atoms/Button";
import { Chip } from "@/Components/v2/atoms/Chip";
import { BrainIcon } from "@/Components/v2/atoms/Icon/BrainIcon";
import { Tooltip } from "@/Components/v2/atoms/ToolTip/Tooltip";

import { UI_CONSTANTS } from "../../constants";
import { PreDefinedConstraints } from "../../types";
import { DocumentUpload } from "./DocumentUpload";

export interface Filter {
  id: string;
  label: string;
}

interface ChatInputProps extends BoxProps {
  placeholder?: string;
  disabled?: boolean;
  filters?: Filter[];
  isAnswerPending: boolean;
  warcEnabledForTier: boolean;
  onAskQuestion: (
    question: string,
    warcEnabled: boolean,
    constraints?: PreDefinedConstraints
  ) => void;
  onDeleteFilter?: (filterId: string) => void;
  onClearAllFilters?: () => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  placeholder = "",
  disabled = false,
  filters = [],
  isAnswerPending,
  warcEnabledForTier,
  onAskQuestion,
  onDeleteFilter,
  onClearAllFilters,
  ...props
}) => {
  const [warcEnabled, setWarcEnabled] = useState<boolean>(warcEnabledForTier);
  const [question, setQuestion] = useState("");
  const { palette } = useTheme();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const isQuestionValid = question.trim().length >= UI_CONSTANTS.MIN_QUESTION_LENGTH;
  const isSubmitDisabled = !isQuestionValid || isAnswerPending || disabled;

  const handleSubmit = useCallback(() => {
    if (isSubmitDisabled) return;
    onAskQuestion(question.trim(), warcEnabled);
    setQuestion("");
  }, [question, warcEnabled, isSubmitDisabled, onAskQuestion]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleAgentToggle = useCallback(() => {
    setWarcEnabled((prev) => !prev);
  }, []);

  const tooltipMessage = useMemo(() => {
    if (!warcEnabledForTier) {
      return "WARC References are a premium feature. Contact us for more information.";
    }
    if (isAnswerPending) {
      return "MAITE may use WARC to answer your question";
    }

    return warcEnabled
      ? "WARC is enabled. MAITE may use WARC to answer your question."
      : "WARC is disabled. Questions will be grounded in your MMM data and general knowledge.";
  }, [warcEnabledForTier, warcEnabled, isAnswerPending]);

  // No filters or WARC toggle in this simplified design

  return (
    <Box
      position="relative"
      mt="auto"
      maxWidth="900px"
      mx="auto"
      {...props}
      borderRadius={3}
      border={1}
      borderColor="#D2D2D2"
      bgcolor={disabled ? palette.action.disabledBackground : palette.background.paper}
      px={2}
      py={1.5}
      pb={0.5}
      display="flex"
      flexDirection="column"
      gap={1.25}
      sx={{
        ...(disabled && {
          opacity: 0.6,
          pointerEvents: "none",
        }),
      }}
    >
      {/* Filter Chips */}
      {filters.length > 0 && (
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          {filters.map((filter) => (
            <Chip
              key={filter.id}
              label={filter.label}
              onDelete={() => onDeleteFilter?.(filter.id)}
            />
          ))}
          <Typography
            component="button"
            onClick={onClearAllFilters}
            sx={{
              color: "link.color",
              fontSize: "12px",
              fontWeight: 500,
              textDecoration: "underline",
              cursor: "pointer",
              background: "none",
              border: "none",
              padding: 0,
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            Clear All Parameters
          </Typography>
        </Stack>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        style={{ flex: 1, position: "relative" }}
      >
        <TextField
          multiline
          minRows={UI_CONSTANTS.TEXTAREA_ROWS}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          fullWidth
          placeholder={placeholder}
          disabled={disabled}
          InputProps={{ disableUnderline: true }}
          variant="standard"
          onKeyDown={handleKeyDown}
          inputRef={inputRef}
          sx={{
            "& .MuiInputBase-root": { alignItems: "flex-start" },
            "& textarea": {
              p: 0,
              m: 0,
              lineHeight: 1.5,
              resize: "none",
              overflow: "auto !important",
            },
            ...(disabled && {
              opacity: 0.6,
              cursor: "not-allowed",
            }),
          }}
        />

        {/* Custom rich placeholder overlay for bold emphasis */}
        {question.trim().length === 0 && (
          <Box
            onClick={() => inputRef.current?.focus()}
            sx={{
              position: "absolute",
              top: 8,
              left: 0,
              color: "text.disabled",
              cursor: "text",
              pointerEvents: "auto",
              pl: "8px",
            }}
          >
            <Typography variant="body1">
              Ask a question about <Box component="span">your growth data</Box>
            </Typography>
          </Box>
        )}

        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="flex-start"
          sx={{ minHeight: "40px" }}
        >
          <FeatureFlagged featureName="growthosHendrenDocumentsInput">
            <Stack direction="row" alignItems="center" height="100%">
              <DocumentUpload hasPendingAction={isAnswerPending} />
            </Stack>
          </FeatureFlagged>

          <FeatureFlagged featureName="enableWarc">
            <Tooltip
              title={tooltipMessage}
              placement="top"
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 280,
                    fontSize: 12,
                    fontWeight: 500,
                  },
                },
              }}
            >
              <span>
                <WarcButton
                  onClick={warcEnabledForTier ? handleAgentToggle : undefined}
                  isPending={isAnswerPending}
                  isFeatureEnabled={warcEnabledForTier}
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      gap: "6px",
                      px: "10px",
                      py: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <BrainIcon
                      status={isAnswerPending ? "pending" : warcEnabled ? "enabled" : "disabled"}
                      disabled={!warcEnabledForTier}
                      sx={{
                        width: "20px",
                        height: "20px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: 1,
                        margin: 0,
                        padding: 0,
                      }}
                    >
                      WARC
                    </Typography>
                  </Stack>
                </WarcButton>
              </span>
            </Tooltip>
          </FeatureFlagged>
          <Stack direction="row" alignItems="center" ml="auto" height="100%">
            <Button
              type="submit"
              disabled={isSubmitDisabled}
              sx={{
                minHeight: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SendIcon opacity={isSubmitDisabled ? 0.5 : 1} />
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};
